import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonInput,
  IonText,
  IonSpinner,
  useIonRouter,
} from '@ionic/react';
import { Button } from '@/components/ui/Button';
import { useMutation } from '@apollo/client/react';
import { ADD_PLAYER_TO_MARKET } from '../../../graphql/mutations/marketEntry';
import { GET_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/activeMarketEntries';
import { GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES } from '../../../graphql/queries/currentFantasyLeagueMemberActiveMarketEntries';
import { useMarketHours } from '@/hooks/useMarketHours';
import type {
  AddPlayerToMarketMutation,
  AddPlayerToMarketMutationVariables,
} from '../../../types/graphql-generated';
import { useState } from 'react';
import { PlayerCard } from '../components/PlayerCard';
import type { Player } from '../../../types/graphql-generated';
import { posthog } from '../../../lib/posthog';

interface AddPlayerPriceModalProps {
  isOpen: boolean;
  player: Player;
  leagueId: string;
  source: 'market_tab' | 'team_tab';
  releaseClause?: number;
  onClose: () => void;
  onSuccess?: () => void;
}

export const AddPlayerPriceModal = ({
  isOpen,
  player,
  leagueId,
  source,
  releaseClause,
  onClose,
  onSuccess,
}: AddPlayerPriceModalProps) => {
  const ionRouter = useIonRouter();
  const { closeTime } = useMarketHours();
  const [price, setPrice] = useState<string>(player.marketValue?.toString() || '');
  const [errors, setErrors] = useState<string[]>([]);

  const [addPlayerToMarket, { loading }] = useMutation<
    AddPlayerToMarketMutation,
    AddPlayerToMarketMutationVariables
  >(ADD_PLAYER_TO_MARKET, {
    refetchQueries: [
      {
        query: GET_ACTIVE_MARKET_ENTRIES,
        variables: { fantasyLeagueId: leagueId },
      },
      {
        query: GET_CURRENT_FANTASY_LEAGUE_MEMBER_ACTIVE_MARKET_ENTRIES,
        variables: { fantasyLeagueId: leagueId },
      },
    ],
  });

  const handleSubmit = async () => {
    setErrors([]);

    const priceValue = parseInt(price, 10);

    if (isNaN(priceValue) || priceValue <= 0) {
      setErrors(['El precio debe ser un número mayor que 0']);
      return;
    }

    try {
      const result = await addPlayerToMarket({
        variables: {
          fantasyLeagueId: leagueId,
          playerId: player.id,
          price: priceValue,
        },
      });

      if (result.data?.addPlayerToMarket?.errors && result.data.addPlayerToMarket.errors.length > 0) {
        setErrors(result.data.addPlayerToMarket.errors);
      } else {
        // Success! Handle based on whether onSuccess callback is provided
        posthog.capture('player_added_to_market', { source });
        if (onSuccess) {
          onSuccess();
          onClose();
        } else {
          onClose();
          ionRouter.goBack();
        }
      }
    } catch (error) {
      setErrors(['Error al añadir el jugador al mercado']);
      console.error('Error adding player to market:', error);
    }
  };

  const handleClose = () => {
    setErrors([]);
    setPrice(player.marketValue?.toString() || '');
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">Precio de venta</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cancelar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="flex flex-col gap-4">
          {/* Player Info */}
          <div>
            <div className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-[var(--fc-ink-3)]">
              Jugador
            </div>
            <PlayerCard key={player.id} player={player} />
          </div>

          {/* Price summary */}
          <div>
            <div className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-[var(--fc-ink-3)]">
              Precio
            </div>
            <div className="rounded-2xl border border-[#e8ebf1] bg-white px-4 shadow-[0_1px_2px_rgba(20,30,60,0.04)]">
              <div className="flex items-center justify-between border-b border-[#eef1f6] py-3 last:border-b-0">
                <span className="text-sm font-semibold text-slate-600">Valor de mercado</span>
                <span className="rounded-lg bg-[#eef1f6] px-3 py-1.5 font-heading text-sm font-extrabold">
                  {player.marketValue?.toLocaleString('es-ES')}€
                </span>
              </div>
              {releaseClause !== undefined && (
                <div className="flex items-center justify-between border-b border-[#eef1f6] py-3 last:border-b-0">
                  <span className="text-sm font-semibold text-slate-600">Cláusula de rescisión</span>
                  <span className="rounded-lg bg-[#eef1f6] px-3 py-1.5 font-heading text-sm font-extrabold">
                    {releaseClause.toLocaleString('es-ES')}€
                  </span>
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              El jugador estará disponible en el mercado hasta mañana a las {closeTime}.
            </p>
          </div>

          {/* Price Input */}
          <IonInput
            className="text-sm font-semibold"
            type="number"
            label="Precio de venta (€)"
            labelPlacement="floating"
            fill="outline"
            value={price}
            onIonInput={(e) => setPrice(e.detail.value || '')}
            placeholder="Introduce el precio"
            min="1"
            step="1"
            inputmode="numeric"
          />

          {/* Errors */}
          {errors.length > 0 && (
            <div>
              {errors.map((error, index) => (
                <IonText color="danger" key={index}>
                  <p className="text-sm">{error}</p>
                </IonText>
              ))}
            </div>
          )}

          {/* Submit Button */}
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleSubmit}
            disabled={loading || !price}
            className="mt-2"
          >
            {loading ? <IonSpinner name="crescent" /> : 'Vender'}
          </Button>
        </div>
      </IonContent>
    </IonModal>
  );
};
